import axios from 'axios';
import { AUTH_BASE_URL } from '../constants';
import { installAuth } from './authInterceptor';

export interface AccessUser {
  id: number;
  username: string;
  email: string | null;
  full_name: string;
  is_active: boolean;
  role_id: number | null;
  role: string | null;
  role_level: number;
  organization_id: number | null;
  organization: string | null;
  last_login_at: string | null;
  /** Requests made to each tool today, most used first. */
  usage_today: { tool_id: string; tool: string; count: number }[];
  department_id: number | null;
  department: string | null;
  /** Tools/sections granted to this user directly, on top of their role. */
  extra_permissions: string[];
  created_at: string;
}

export interface AccessRole {
  id: number;
  name: string;
  description: string | null;
  level: number;
  permissions: string[];
  /** Whether the signed-in user may change this role's permissions. */
  editable: boolean;
}

export interface AccessPermission {
  id: number;
  name: string;
  description: string | null;
  /** `section` and `tool` permissions are generated from the tool catalog. */
  kind: 'system' | 'section' | 'tool';
  section: string | null;
  label: string;
  order: number;
}

export interface Organization {
  id: number;
  name: string;
  code: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Department {
  id: number;
  /** Tools/sections every member of the department gets. */
  permissions: string[];
  name: string;
  code: string | null;
  is_active: boolean;
  created_at: string;
}

export interface AuditEntry {
  id: number;
  username: string;
  action: string;
  detail: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditPage {
  total: number;
  items: AuditEntry[];
  actions: string[];
}

export interface UserCreateInput {
  username: string;
  password: string;
  email?: string;
  full_name?: string;
  role_id: number;
  organization_id: number | null;
  department_id: number | null;
}

export type UserUpdateInput = Partial<{
  email: string | null;
  full_name: string;
  password: string;
  role_id: number;
  organization_id: number | null;
  department_id: number | null;
  is_active: boolean;
}>;

const http = axios.create({ baseURL: `${AUTH_BASE_URL}/admin` });

installAuth(http);

function messageFrom(error: unknown): string {
  if (!axios.isAxiosError(error)) return 'Request failed';
  if (error.response?.status === 401) {
    return 'Your session has expired. Sign out and sign in again.';
  }
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: { loc?: string[]; msg?: string }) => `${d.loc?.slice(-1)[0] ?? ''}: ${d.msg ?? ''}`).join('; ');
  }
  return error.response ? `Request failed (${error.response.status})` : 'Could not reach the backend.';
}

async function call<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data;
  } catch (error) {
    throw new Error(messageFrom(error));
  }
}

export const fetchUsers = () => call(http.get<{ users: AccessUser[] }>('/users')).then((d) => d.users);
export const createUser = (body: UserCreateInput) => call(http.post<AccessUser>('/users', body));
export const updateUser = (id: number, body: UserUpdateInput) =>
  call(http.patch<AccessUser>(`/users/${id}`, body));

export const saveDepartmentPermissions = (id: number, permissions: string[]) =>
  call(http.put<Department>(`/departments/${id}/permissions`, { permissions }));

export const fetchDepartments = () =>
  call(http.get<{ departments: Department[] }>('/departments')).then((d) => d.departments);
export const createDepartment = (body: { name: string; code?: string }) =>
  call(http.post<Department>('/departments', body));
export const updateDepartment = (
  id: number,
  body: Partial<{ name: string; code: string | null; is_active: boolean }>,
) => call(http.patch<Department>(`/departments/${id}`, body));

export const fetchRoles = () =>
  call(http.get<{ roles: AccessRole[]; permissions: AccessPermission[]; grantable: string[] }>('/roles'));
export const saveRolePermissions = (roleId: number, permissions: string[]) =>
  call(http.put(`/roles/${roleId}/permissions`, { permissions }));

export const fetchOrganizations = () =>
  call(http.get<{ organizations: Organization[] }>('/organizations')).then((d) => d.organizations);
export const createOrganization = (body: { name: string; code?: string }) =>
  call(http.post<Organization>('/organizations', body));
export const updateOrganization = (
  id: number,
  body: Partial<{ name: string; code: string | null; is_active: boolean }>,
) => call(http.patch<Organization>(`/organizations/${id}`, body));

export const fetchAuditLogs = (params: {
  action?: string;
  username?: string;
  limit: number;
  offset: number;
}) => call(http.get<AuditPage>('/audit-logs', { params }));

export interface UsageReport {
  date: string;
  items: { username: string; full_name: string; tool_id: string; tool: string; count: number }[];
  by_tool: { tool_id: string; tool: string; count: number }[];
}

export const fetchUsage = (date: string) => call(http.get<UsageReport>('/usage', { params: { date } }));

// ---------------------------------------------------------------- access requests

export interface AccessRequestInput {
  username: string;
  password: string;
  email: string;
  full_name: string;
  company?: string;
  department_id: number;
  reason?: string;
}

export interface AccessRequestOptions {
  departments: { id: number; name: string }[];
  companies: { id: number; name: string }[];
}

/** Public: choices for the sign-up form's department and company dropdowns. */
export async function fetchAccessRequestOptions(): Promise<AccessRequestOptions> {
  try {
    return (await axios.get<AccessRequestOptions>(`${AUTH_BASE_URL}/access-requests/options`)).data;
  } catch (error) {
    throw new Error(messageFrom(error));
  }
}

export interface AccessRequestItem {
  id: number;
  username: string;
  email: string | null;
  full_name: string;
  company: string;
  department: string | null;
  reason: string;
  status: 'pending' | 'approved' | 'declined';
  review_note: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/** Public sign-up from the login page: no token, so it uses a plain axios call. */
export async function submitAccessRequest(body: AccessRequestInput): Promise<string> {
  try {
    const response = await axios.post<{ message: string }>(`${AUTH_BASE_URL}/access-requests`, body);
    return response.data.message;
  } catch (error) {
    throw new Error(messageFrom(error));
  }
}

export const fetchAccessRequests = (status?: AccessRequestItem['status']) =>
  call(
    http.get<{ requests: AccessRequestItem[]; pending_count: number }>('/access-requests', {
      params: status ? { status } : undefined,
    }),
  );
export const approveAccessRequest = (id: number, role_id: number, organization_id: number | null) =>
  call(http.post<AccessRequestItem>(`/access-requests/${id}/approve`, { role_id, organization_id }));
export const declineAccessRequest = (id: number, note: string) =>
  call(http.post<AccessRequestItem>(`/access-requests/${id}/decline`, { note }));
